import { JRCList, PaginationPagination } from 'components/JRCList/JRCList';
import { JRCLoader } from 'components/JRCLoader/JRCLoader';
import { BaseAutocomplete, PagingResults } from 'jamespot-user-api';
import * as React from 'react';
import { FieldValues, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Column } from 'react-table';
import { deepEqual } from 'utils/utils.object';
import { JRCMessage } from '../../../JRCMessage/JRCMessage';
import { JRCModalButtonType } from '../../../JRCModal/JRCModalContent';
import { JRCModal } from '../../../JRCModal/templates/JRCModal';
import { JRCModalProps } from '../../../JRCModal/templates/JRCModal.types';
import * as Components from './JRCInputSelect.style';
import { IconsProp, JRCInputSelectProps } from './JRCInputSelect.types';
import { JRCInputSelectExtended } from './JRCInputSelectExtended';

export type JRCInputSelectListProps<T extends FieldValues = FieldValues> = Omit<
    JRCInputSelectProps<T>,
    'asyncPromise'
> &
    Pick<JRCModalProps, 'inPlace'> & {
        asyncPromise?: (inputValue: string, page?: number) => Promise<PagingResults<any>>;
        displayModal?: boolean;
        onChange: (values: BaseAutocomplete[]) => void;
    };

export const JRCInputSelectList = <T extends FieldValues = FieldValues>({
    asyncPromise,
    displayModal = true,
    inPlace = false,
    width,
    placeholder,
    onChange,
    control,
    name,
    allowCustomOptions,
    ...props
}: JRCInputSelectListProps<T>) => {
    const intl = useIntl();

    const selectedOptions: BaseAutocomplete[] | undefined = useWatch({ control, name });
    const ModalButtons: JRCModalButtonType[] = [
        {
            color: 'valid',
            children: <FormattedMessage id="GLOBAL_Save" />,
            clickHandler: () => {
                setOpen(false);
            },
        },
    ];

    const IconButtonBefore: IconsProp['iconBefore'] = {
        color: 'grey2',
        icon: 'icon-search',
        onClick: () => setOpen(!isOpen),
    };

    const IconBefore: IconsProp['iconBefore'] = {
        color: 'grey2',
        icon: 'icon-search',
    };

    const [isOpen, setOpen] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [value, setValue] = React.useState<string>('');
    const [page, setPage] = React.useState<number>(1);
    const [data, setData] = React.useState<Record<string, unknown>[]>([]);
    const [config, setConfig] = React.useState<{
        manualSortBy?: boolean;
        draggable?: boolean;
        pagination?: PaginationPagination;
    }>({
        manualSortBy: true,
        draggable: false,
        pagination: {
            page: 1,
            perPage: 1,
            nbResults: 1,
        },
    });

    const asyncPromiseFunction = React.useMemo(
        () => (inputValue: string) => {
            if (
                asyncPromise &&
                ((isOpen && !inputValue && data && data.length === 0) ||
                    (isOpen &&
                        page !== config.pagination?.page &&
                        deepEqual(value.toLowerCase(), inputValue.toLowerCase())) ||
                    (!isOpen && inputValue !== '' && !deepEqual(value.toLowerCase(), inputValue.toLowerCase())) ||
                    (isOpen && !deepEqual(value.toLowerCase(), inputValue.toLowerCase())))
            ) {
                setIsLoading(true);
                if (!deepEqual(value.toLowerCase(), inputValue.toLowerCase())) setValue(inputValue);
                const pagination = !deepEqual(value.toLowerCase(), inputValue.toLowerCase()) ? 1 : page;
                return asyncPromise(inputValue, pagination).then((response: any) => {
                    if (response?.data) {
                        setConfig({
                            manualSortBy: true,
                            draggable: false,
                            pagination: {
                                page: response.page,
                                perPage: response.limit,
                                nbResults: response.cnt,
                            },
                        });
                        setData(response.data);
                    }
                    setIsLoading(false);
                    return response?.data || [];
                });
            }
            return Promise.resolve([]);
        },
        [asyncPromise, isOpen, data, page, config.pagination?.page, value],
    );

    React.useEffect(() => {
        if (asyncPromise && page) {
            asyncPromiseFunction(value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    React.useEffect(() => {
        if (value) {
            setValue('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    React.useEffect(() => {
        if (!isOpen && value == '') {
            setData([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const eventHandlers = {
        onPage: (page: number) => {
            setPage(page);
        },
    };

    const columns: Array<Column<any>> = [
        {
            Header: 'Title',
            accessor: (option) => {
                const optionIndex =
                    selectedOptions?.findIndex((opt: BaseAutocomplete) => deepEqual(opt, option)) || null;
                return (
                    <Components.OptionSelectCell
                        disabled={
                            optionIndex === -1 &&
                            !!props.limit &&
                            selectedOptions &&
                            selectedOptions.length >= props.limit
                        }
                        isSelected={optionIndex !== -1}
                        onClick={() => {
                            if (optionIndex && optionIndex !== -1) {
                                const optionsSelectedCopy = [...(selectedOptions ?? [])];
                                optionsSelectedCopy.splice(optionIndex, 1);
                                onChange(optionsSelectedCopy);
                            } else if (optionIndex === -1) {
                                onChange([...(selectedOptions ?? []), option]);
                            }
                        }}>
                        {option?.title}
                    </Components.OptionSelectCell>
                );
            },
            disableSortBy: true,
        },
    ];

    return (
        <Components.InputSelectSearch>
            <JRCInputSelectExtended
                asyncPromise={!isOpen ? asyncPromiseFunction : () => Promise.resolve([])}
                name={name}
                control={control}
                showAsyncResultOnEmptySearch={true}
                multiple={true}
                displayOption={true}
                width={width}
                placeholder={placeholder}
                iconBefore={IconButtonBefore}
                allowCustomOptions={allowCustomOptions}
                {...props}
            />
            {displayModal && (
                <JRCModal
                    title={placeholder || ''}
                    open={isOpen}
                    closeHandler={() => setOpen(false)}
                    portalId="react-modal"
                    inPlace={inPlace}
                    buttons={ModalButtons}>
                    {props.limit && selectedOptions?.length && selectedOptions.length >= props.limit && (
                        <JRCMessage
                            variant={'warning'}
                            message={intl.formatMessage(
                                { id: 'INPUT_SELECT_Has_Reached_Limit' },
                                { limit: props.limit },
                            )}
                        />
                    )}
                    <Components.ModalContentInputSelectList>
                        <Components.ModalContentInputSelect>
                            <JRCInputSelectExtended
                                asyncPromise={asyncPromiseFunction}
                                name={name}
                                control={control}
                                showAsyncResultOnEmptySearch={true}
                                displayOption={false}
                                width="100%"
                                placeholder=""
                                displayIndicator={false}
                                iconBefore={IconBefore}
                                {...props}
                            />
                        </Components.ModalContentInputSelect>
                        <Components.WrapperList>
                            {isLoading ? (
                                <Components.NoOptionList direction="row" x="center" y="center">
                                    <JRCLoader size={'m'} />
                                </Components.NoOptionList>
                            ) : !data.length ? (
                                <Components.NoOptionList direction="row" x="center" y="center">
                                    <FormattedMessage id="GLOBAL_No_Result" />
                                </Components.NoOptionList>
                            ) : (
                                !!data.length &&
                                config && (
                                    <Components.ModalContentList>
                                        <JRCList
                                            config={config}
                                            data={data}
                                            columns={columns}
                                            eventHandlers={eventHandlers}
                                        />
                                    </Components.ModalContentList>
                                )
                            )}
                        </Components.WrapperList>
                    </Components.ModalContentInputSelectList>
                </JRCModal>
            )}
        </Components.InputSelectSearch>
    );
};
